import { useMutation } from '@apollo/client'
import { HighnoteClient as client } from '@/src/services/Highnote'
import { useLogError } from '@/src/common/hooks/useLogError'
import { routerBack } from '@/src/common/utils/routerBack'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import {
  FREEZE_PAYMENT_CARD,
  UNFREEZE_PAYMENT_CARD,
} from '@/src/app/main/(tabs)/card/card-management/virtual-card/highnote'
import { graphql } from '@api/mesa'
import { useCombinedCardStatusStore } from '@/src/experiences/virtual-card/store/useCombinedCardStatusStore'
import { z } from 'zod/v4'
import type { FreezeProps } from '@/src/experiences/virtual-card/withFreeze'
import type { FunctionComponent } from 'react'
import { useLegacyPaymentCard } from '@/src/app/main/(tabs)/card/card-management/virtual-card/highnote/useLegacyPaymentCard'

export const LegacyFreezeParams = z.object({ paymentCardId: z.string() })

interface Props extends z.infer<typeof LegacyFreezeParams> {
  ToWrap: FunctionComponent<FreezeProps>
}

export function LegacyFreeze({ paymentCardId, ToWrap }: Props) {
  useLegacyPaymentCard({ variables: paymentCardId ? { paymentCardId } : undefined })

  const [generateManageCardClientToken] = useMutation(
    graphql(`
      mutation GenerateManageCardClientToken {
        generateManageCardClientToken {
          clientToken
        }
      }
    `),
  )
  const [freezePaymentCard, { loading: freezing }] = useMutation(FREEZE_PAYMENT_CARD, { client })
  const [unfreezePaymentCard, { loading: unfreezing }] = useMutation(UNFREEZE_PAYMENT_CARD, {
    client,
  })
  const { setStatus } = useCombinedCardStatusStore()
  const logError = useLogError()
  const Alert = useAlert(LegacyFreeze.name)

  async function toggleFreeze(isFrozen: boolean) {
    const manageCardClientTokenResponse = await generateManageCardClientToken()
    const manageCardClientToken =
      manageCardClientTokenResponse.data?.generateManageCardClientToken.clientToken

    if (isFrozen) {
      const { data } = await unfreezePaymentCard({
        variables: { input: { paymentCardId } },
        context: { clientToken: manageCardClientToken },
      })
      if (
        data?.activatePaymentCard?.__typename !== 'PaymentCard' ||
        !data.activatePaymentCard.status
      ) {
        logError(new Error('Failed to unfreeze card', { cause: data }))
        Alert.alert('Failed to unfreeze card', 'Please try again later.')
      } else {
        setStatus(data.activatePaymentCard.status)
        void routerBack()
      }
    } else {
      const { data } = await freezePaymentCard({
        variables: { input: { paymentCardId } },
        context: { clientToken: manageCardClientToken },
      })
      if (
        data?.suspendPaymentCard?.__typename !== 'PaymentCard' ||
        !data.suspendPaymentCard.status
      ) {
        logError(new Error('Failed to freeze card', { cause: data }))
        Alert.alert('Failed to freeze card', 'Please try again later.')
      } else {
        setStatus(data.suspendPaymentCard.status)
        void routerBack()
      }
    }
  }

  return <ToWrap toggleFreeze={toggleFreeze} freezing={freezing} unfreezing={unfreezing} />
}
